import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CatalogProduct, CustomerProductPriceResult, ProductListProduct, UnitOfMeasureOrderQuantity } from '../../api';
import { ProductDetailTransfer } from '../../api/models/product-detail.models';
import {
  ProductShoppingCategoryProductSearchRequest,
  ProductShoppingCategoryProductSearchResult,
} from '../../api/models/product-shopping-category-search.models';
import { normalizeProductKey } from '../../helpers';
import { createPagedEntityAdapter, EntityPageLoading, PagedEntityState } from '../paged-entity-adapter';
import { RootState } from '../store';

const productsAdapter = createPagedEntityAdapter<CatalogProduct>({
  selectId: (product) => normalizeProductKey(product.ProductKey),
});

export interface ProductShoppingCategorySearchState {
  apiRequest?: ProductShoppingCategoryProductSearchRequest;
  searchResult?: Omit<ProductShoppingCategoryProductSearchResult, 'CatalogProducts'>;
  products: PagedEntityState<CatalogProduct>;
}

export const initialState: ProductShoppingCategorySearchState = {
  apiRequest: undefined,
  searchResult: undefined,
  products: productsAdapter.getInitialState(false),
};

export const productShoppingCategorySearchSlice = createSlice({
  name: 'productShoppingCategorySearch',
  initialState,
  reducers: {
    resetResults: (state) => {
      state.apiRequest = undefined;
      state.searchResult = undefined;
      state.products = initialState.products;
    },

    setApiRequest: (state, action: PayloadAction<ProductShoppingCategoryProductSearchRequest | undefined>) => {
      state.apiRequest = action.payload;
    },

    setSearchResults: (
      state,
      action: PayloadAction<{
        request: ProductShoppingCategoryProductSearchRequest;
        result: ProductShoppingCategoryProductSearchResult;
        pageIndex: number;
      }>
    ) => {
      const { request, result, pageIndex } = action.payload;

      state.apiRequest = request;
      state.searchResult = {
        CurrentPageNumber: result.CurrentPageNumber,
        NumberOfPages: result.NumberOfPages,
        PageSize: result.PageSize,
        QueryText: result.QueryText,
        TotalNumberOfItems: result.TotalNumberOfItems,
        Skip: result.Skip,
        Facets: result.Facets,
        HasLoadMore: result.HasLoadMore,
        TotalCount: result.TotalCount,
      };

      productsAdapter.setLoadedPage(state.products, {
        pageIndex,
        entities: result.CatalogProducts,
      });
    },

    setPageLoaded: (state, action: PayloadAction<{ pageIndex: number; catalogProducts: CatalogProduct[] }>) => {
      productsAdapter.setLoadedPage(state.products, {
        pageIndex: action.payload.pageIndex,
        entities: action.payload.catalogProducts,
      });
    },

    setPageLoading: (state, action: PayloadAction<EntityPageLoading>) => {
      productsAdapter.setLoadingPage(state.products, action.payload);
    },
    updateProduct: (state, action: PayloadAction<ProductDetailTransfer>) => {
      const product = state.products.entities[normalizeProductKey(action.payload.productKey)];
      if (product) {
        product.UnitOfMeasureOrderQuantities.forEach((uom: UnitOfMeasureOrderQuantity) => {
          if (uom.UnitOfMeasure === action.payload.uom) {
            uom.ExtendedPrice = action.payload.extendedPrice;
            uom.Quantity = action.payload.quantity;
            uom.ShowOrderQuantityAlert = action.payload.showOrderQuantityAlert;
          }
        });
      }
    },
    setProductPrices: (state, action: PayloadAction<CustomerProductPriceResult[]>) => {
      action.payload.forEach((priceResult: CustomerProductPriceResult) => {
        if (priceResult.ProductKey) {
          const product = state.products.entities[normalizeProductKey(priceResult.ProductKey)] as ProductListProduct;
          if (product) {
            product.PriceLoaded = true;
            product.UnitOfMeasureOrderQuantities.forEach((uom: UnitOfMeasureOrderQuantity) => {
              if (uom.UnitOfMeasure === priceResult.UnitOfMeasureType) {
                uom.Price = priceResult.Price;
                uom.SecondaryUnitPrice = priceResult.SecondaryUnitPrice;
                if (priceResult.ExtendedPrice) {
                  uom.ExtendedPrice = priceResult.ExtendedPrice;
                }
                if (priceResult.ProductAverageWeight > 0) {
                  uom.ProductAverageWeight = priceResult.ProductAverageWeight;
                }
              }
            });
          }
        }
      });
    },
  },
});

/* ===================== SELECTORS ===================== */

export const {
  selectById: selectCategorySearchProductById,
  selectAll: selectAllCategorySearchProducts,
  selectByPage: selectCategorySearchProductsByPage,
  selectIsLoading: selectCategorySearchIsLoading,
  selectTotal: selectCategorySearchTotal,
} = productsAdapter.getSelectors<RootState>((state) => state.productShoppingCategorySearch.products);

export const selectCategorySearchResult = (state: RootState) => state.productShoppingCategorySearch.searchResult;

export const selectCategorySearchRequest = (state: RootState) => state.productShoppingCategorySearch.apiRequest;

export const selectCategorySearchGridPages = createSelector(
  (state: RootState) => state.productShoppingCategorySearch.products.pages,
  (pages) => pages
);

export default productShoppingCategorySearchSlice.reducer;
